import React, { useState, useEffect, useRef } from 'react';
import { useStore } from '@nanostores/react';
import { isSearchOpen, searchTerm } from '@stores/searchStore';
import { track } from '@vercel/analytics';

import SearchFlyoutNosto from './SearchFlyout/SearchFlyoutNosto.jsx';
import { getSearchResults } from '../../api/nosto.js';
import SearchIcon from './SearchIcon/SearchIcon.jsx';
import SearchStatic from './SearchStatic/SearchStatic.jsx';
import staticData from './SearchData/staticData.js';
import { filterNostoProducts } from '@utils/nostoValidationUtils.js';
import { getNostoSessionParamsWithFallback } from '@utils/nostoSessionUtils.js';
import { recordSearchSubmit, recordSearchAutocomplete } from '@utils/nostoTracking.js';

const SearchNosto = () => {
    const $isSearchOpen = useStore(isSearchOpen || false);
    const $searchTerm = useStore(searchTerm || '');

    const [query, setQuery] = useState($searchTerm.q || '');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hitCount, setHitCount] = useState(0);
    const [suggestions, setSuggestions] = useState([]);
    const [searched, setSearched] = useState(false);
    const [collections, setCollections] = useState([]);
    const [pages, setPages] = useState([]);
    const [redirect, setRedirect] = useState(null);
    const debounceRef = useRef();
    const inputElement = useRef(null);

    const handleSearch = async (searchTerm = query) => {
        if (!searchTerm.trim()) {
            setResults([]);
            setHitCount(0);
            setSearched(false);
            setCollections([]);
            setPages([]);
            setRedirect(null);
            return;
        }
        setLoading(true);

        // Get session params for personalization with timeout
        let sessionParams = null;
        try {
            const sessionPromise = getNostoSessionParamsWithFallback();
            const timeoutPromise = new Promise((resolve) => setTimeout(() => resolve(null), 3000));
            sessionParams = await Promise.race([sessionPromise, timeoutPromise]);
        } catch (error) {
            sessionParams = null;
        }

        const searchResults = await getSearchResults(searchTerm, 500, sessionParams);

        const hits = searchResults?.data?.search?.products?.hits || [];
        const filtered = filterNostoProducts(hits, {
            includeOutOfStock: true,
            enableLogging: true,
            context: `Search Component: "${searchTerm}"`
        });
        setResults(filtered);
        setHitCount(filtered.length);
        setLoading(false);
        setSearched(true);
        setRedirect(searchResults?.data?.search?.redirect || null);

        // Record search submit analytics
        if (filtered.length > 0) {
            recordSearchSubmit(searchTerm, filtered);
        }
    };

    const handleInput = (val) => {
        clearTimeout(debounceRef.current);
        if (!val.trim()) {
            setSuggestions([]);
            setSearched(false);
            setCollections([]);
            setPages([]);
            setRedirect(null);
            return;
        }
        debounceRef.current = setTimeout(async () => {
            try {
                // Get session params for personalization with timeout
                let sessionParams = null;
                try {
                    const sessionPromise = getNostoSessionParamsWithFallback();
                    const timeoutPromise = new Promise((resolve) => setTimeout(() => resolve(null), 3000));
                    sessionParams = await Promise.race([sessionPromise, timeoutPromise]);
                } catch (error) {
                    sessionParams = null;
                }

                // PERFORMANCE OPTIMIZATION: Limit search flyout results to 8 for faster loading
                let results;
                try {
                    results = await getSearchResults(val, 8, sessionParams);
                } catch (error) {
                    results = await getSearchResults(val, 8, null);
                }

                const keywords = results?.data?.search?.keywords?.hits || [];

                if (Array.isArray(keywords) && keywords.length > 0) {
                    const keywordList = keywords.map(k => k.keyword);
                    setSuggestions(keywordList);
                } else {
                    setSuggestions([]);
                }

                // Handle search results separately with enhanced filtering
                if (results?.data?.search?.products?.hits?.length > 0) {
                    const hits = results.data.search.products.hits;

                    const filtered = filterNostoProducts(hits, {
                        includeOutOfStock: true,
                        enableLogging: true,
                        context: `Search Suggestions: "${val}"`
                    });

                    setResults(filtered);
                    setHitCount(filtered.length);
                    setSearched(true);

                    // Record autocomplete analytics
                    if (filtered.length > 0) {
                        console.log('🔍 SearchNosto - Calling recordSearchAutocomplete with:', {
                            query: val,
                            filteredCount: filtered.length,
                            sampleFiltered: filtered[0]
                        });
                        recordSearchAutocomplete(val, filtered);
                    }
                } else {
                    setResults([]);
                    setHitCount(0);
                    setSearched(true);
                }

                // Handle collections and pages
                setCollections(results?.data?.search?.collections?.hits || []);
                setPages(results?.data?.search?.pages?.hits || []);

                // Handle redirect
                setRedirect(results?.data?.search?.redirect || null);
            } catch (error) {
                console.error('🔍 SearchNosto - Error fetching search results:', error);
                setSuggestions([]);
                setCollections([]);
                setPages([]);
                setRedirect(null);
            }
        }, 300);
    };

    const handleSuggestionClick = (keyword) => {
        setQuery(keyword);
        handleSearch(keyword);
    };

    const handleClearSearch = () => {
        setQuery('');
        setResults([]);
        setHitCount(0);
        setSearched(false);
        setSuggestions([]);
        setRedirect(null);
    };

    const handleCancelSearch = () => {
        if (isSearchOpen) {
            isSearchOpen.set(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            isSearchOpen.set(false);
            setTimeout(() => {
                window.location.href = `/search/${encodeURIComponent(query)}`;
            }, 100);
        }
        if (e.key === 'Escape') {
            handleCancelSearch();
        }
    };

    // Handle input changes including paste events
    const handleInputChange = (e) => {
        const value = e.target.value;
        setQuery(value);
        handleInput(value);
    };

    // Handle paste events specifically
    const handlePaste = (e) => {
        // Let the onChange handle the paste, but ensure it triggers
        setTimeout(() => {
            const value = e.target.value;
            if (value && value !== query) {
                handleInput(value);
            }
        }, 0);
    };

    useEffect(() => {
        if (inputElement.current) {
            inputElement.current.focus();
        }
    }, []);

    // Focus input when search modal opens
    useEffect(() => {
        if ($isSearchOpen && inputElement.current) {
            // Small delay to ensure the modal is fully rendered
            setTimeout(() => {
                inputElement.current.focus();
            }, 50);
        }
    }, [$isSearchOpen]);

    useEffect(() => {
        const keyDownHandler = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                handleCancelSearch();
            }
        };

        document.addEventListener('keydown', keyDownHandler);
        return () => {
            document.removeEventListener('keydown', keyDownHandler);
        };
    }, []);

    // Watch for query changes and update suggestions
    useEffect(() => {
        if (query && query.trim()) {
            // Debounce the suggestion update
            const timer = setTimeout(() => {
                handleInput(query);
            }, 300);
            return () => clearTimeout(timer);
        } else {
            setSuggestions([]);
        }
    }, [query]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (query) {
                track('Search', { query: query });
            }
        }, 2000);
        return () => clearTimeout(timer);
    }, [query]);

    // Search term tracking - similar to Search.jsx
    useEffect(() => {
        // Setup before functions
        let typingTimer; // timer identifier
        let doneTypingInterval = 2000; // time in ms (2 seconds)
        let myInput = document.getElementById('nosto-search-input');

        if (!myInput) return;

        // On keyup, start the countdown
        const handleKeyUp = () => {
            clearTimeout(typingTimer);
            if (myInput.value) {
                typingTimer = setTimeout(doneTyping, doneTypingInterval);
            }
        };

        // User is "finished typing," do something
        function doneTyping() {

            dataLayer = window.dataLayer || [];
            dataLayer.push({
                event: 'GA4_search_term',
                search_term: query,
            });
        }

        myInput.addEventListener('keyup', handleKeyUp);

        // Cleanup
        return () => {
            if (myInput) {
                myInput.removeEventListener('keyup', handleKeyUp);
            }
            clearTimeout(typingTimer);
        };
    }, [$isSearchOpen, query]);

    return (
        <div
            className={`${
                $isSearchOpen
                    ? 'fixed top-[60px] left-1/2 -translate-x-1/2 w-full max-w-[1200px] bg-white z-[200] max-h-[calc(100vh-60px)] flex flex-col'
                    : 'hidden'
            }`}
        >
            <div className="w-full flex-shrink-0">
                <div className='search-input-outer h-16 w-full border bottom-1 border-stone-300'>
                    <div className='search-input-inner flex items-center p-2 h-full relative ml-1 lg:ml-4'>
                        <span className='search-icon w-5 absolute left-6'>
                            <SearchIcon />
                        </span>
                        <input
                            className={`w-full h-12 mr-4 pl-10 pr-3 border-2 border-oat text-xl text-ash appearance-none ${
                                query ? 'text-2xl' : 'font-normal'
                            }`}
                            id='nosto-search-input'
                            type='search'
                            placeholder='Search here...'
                            value={query}
                            onChange={handleInputChange}
                            onInput={handleInputChange}
                            onPaste={handlePaste}
                            autoFocus
                            ref={inputElement}
                            onKeyDown={handleKeyPress}
                            inputMode='search'
                            autoCapitalize='none'
                        />
                        {query ? (
                            <div
                                className={`search-clear flex items-center absolute right-[90px] lg:right-[130px] ${
                                    query.length > 18
                                        ? 'hidden lg:flex'
                                        : ''
                                }`}
                            >
                                <div
                                    className='clear-search'
                                    onClick={handleClearSearch}
                                >
                                    <span className='clear-text mr-2 text-sm cursor-pointer text-oat hover:text-flint relative -top-[1px]'>
                                        Clear
                                    </span>
                                </div>
                            </div>
                        ) : null}

                        <div className='cancel-search bg-slate px-3 rounded-lg mr-2'>
                            <span
                                className='cancel-icon flex items-center text-flint hover:text-ash relative  cursor-pointer text-xs lg:text-base'
                                onClick={handleCancelSearch}
                            >
                                <span className='text-2xl -top-[2px] relative lg:mr-1'>
                                    &times;
                                </span>{' '}
                                <span className='hidden lg:block'>Close</span>
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex-1 overflow-y-auto">
                {query && (
                    <SearchFlyoutNosto
                        suggestions={suggestions}
                        handleSuggestionClick={handleSuggestionClick}
                        results={results}
                        query={query}
                        collections={collections}
                        pages={pages}
                        loading={loading}
                        searched={searched}
                        redirect={redirect}
                    />
                )}
                {!query ? (
                    <SearchStatic
                        staticData={staticData}
                        handleSuggestionClick={handleSuggestionClick}
                    />
                ) : null}
            </div>
        </div>
    );
};

export default SearchNosto;
